import type { NostrEvent } from '@nostrify/nostrify';

export const MUSIC_TRACK_KIND = 36787;
export const PLAYLIST_KIND = 34139;

export interface MusicTrack {
  event: NostrEvent;
  id: string;
  pubkey: string;
  title: string;
  audioUrl: string;
  imageUrl?: string;
  gradient?: string[];
  duration?: number;
  genres: string[];
  isAiGenerated?: boolean;
  createdAt: number;
  artistName?: string;
  artistPubkey?: string;
}

/**
 * Validate a music track event
 */
export function validateMusicTrack(event: NostrEvent): boolean {
  if (event.kind !== MUSIC_TRACK_KIND) return false;

  const d = event.tags.find(([name]) => name === 'd')?.[1];
  const title = event.tags.find(([name]) => name === 'title')?.[1];
  const url = event.tags.find(([name]) => name === 'url')?.[1];

  // Required fields
  if (!d || !title || !url) return false;

  return true;
}

/**
 * Parse a music track event into a MusicTrack object
 */
export function parseMusicTrack(event: NostrEvent): MusicTrack | null {
  if (!validateMusicTrack(event)) return null;

  const d = event.tags.find(([name]) => name === 'd')?.[1]!;
  const title = event.tags.find(([name]) => name === 'title')?.[1]!;
  const audioUrl = event.tags.find(([name]) => name === 'url')?.[1]!;
  const imageUrl = event.tags.find(([name]) => name === 'image')?.[1];
  const gradientTag = event.tags.find(([name]) => name === 'gradient');
  const gradient = gradientTag?.[2]?.split(',').filter(Boolean);
  const durationStr = event.tags.find(([name]) => name === 'duration')?.[1];
  const duration = durationStr ? parseInt(durationStr) : undefined;

  // Get all genre tags (excluding "music" tag)
  const genres = event.tags
    .filter(([name]) => name === 't')
    .map(([, value]) => value)
    .filter((tag) => tag && tag !== 'music');

  // Check if track is AI-generated
  const isAiGenerated = event.tags.some(
    ([name, value]) => name === 'ai-generated' && value === 'true'
  );

  // Get artist information
  const artistName = event.tags.find(([name]) => name === 'artist')?.[1];
  const artistPubkey = event.tags.find(([name]) => name === 'p')?.[1];

  return {
    event,
    id: d,
    pubkey: event.pubkey,
    title,
    audioUrl,
    imageUrl,
    gradient,
    duration,
    genres,
    isAiGenerated,
    createdAt: event.created_at,
    artistName,
    artistPubkey,
  };
}

/**
 * Generate a random gradient for tracks without artwork
 */
export function generateRandomGradient(): string[] {
  const hues = [
    [330, 350], // Red-pink
    [200, 220], // Blue
    [280, 300], // Purple
    [150, 180], // Cyan-green
    [30, 50],   // Orange
    [260, 280], // Violet
  ];

  const randomHueRange = hues[Math.floor(Math.random() * hues.length)];
  const hue1 = Math.floor(Math.random() * (randomHueRange[1] - randomHueRange[0]) + randomHueRange[0]);
  const hue2 = (hue1 + 30 + Math.floor(Math.random() * 60)) % 360;
  const hue3 = (hue1 + 120 + Math.floor(Math.random() * 60)) % 360;

  return [
    `hsl(${hue1}, 70%, 50%)`,
    `hsl(${hue2}, 70%, 50%)`,
    `hsl(${hue3}, 70%, 50%)`,
  ];
}

/**
 * Format duration in seconds to MM:SS
 */
export function formatDuration(seconds?: number): string {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export interface Playlist {
  event: NostrEvent;
  id: string;
  pubkey: string;
  title: string;
  description?: string;
  imageUrl?: string;
  gradient?: string[];
  trackRefs: string[]; // Array of 'a' tag values (36787:pubkey:d-tag)
  tags: string[];
  isPublic: boolean;
  isCollaborative: boolean;
  createdAt: number;
}

/**
 * Validate a playlist event
 */
export function validatePlaylist(event: NostrEvent): boolean {
  if (event.kind !== PLAYLIST_KIND) return false;

  const d = event.tags.find(([name]) => name === 'd')?.[1];
  const title = event.tags.find(([name]) => name === 'title')?.[1];

  // Required fields
  if (!d || !title) return false;

  return true;
}

/**
 * Parse a playlist event into a Playlist object
 */
export function parsePlaylist(event: NostrEvent): Playlist | null {
  if (!validatePlaylist(event)) return null;

  const d = event.tags.find(([name]) => name === 'd')?.[1]!;
  const title = event.tags.find(([name]) => name === 'title')?.[1]!;
  const description = event.tags.find(([name]) => name === 'description')?.[1];
  const imageUrl = event.tags.find(([name]) => name === 'image')?.[1];
  const gradientTag = event.tags.find(([name]) => name === 'gradient');
  const gradient = gradientTag?.[2]?.split(',').filter(Boolean);

  // Get all track references
  const trackRefs = event.tags
    .filter(([name]) => name === 'a')
    .map(([, value]) => value)
    .filter(Boolean);

  // Get all category tags (excluding "playlist" tag)
  const tags = event.tags
    .filter(([name]) => name === 't')
    .map(([, value]) => value)
    .filter((tag) => tag && tag !== 'playlist');

  const isPublic = event.tags.some(([name, value]) => name === 'public' && value === 'true');
  const isCollaborative = event.tags.some(([name, value]) => name === 'collaborative' && value === 'true');

  return {
    event,
    id: d,
    pubkey: event.pubkey,
    title,
    description: description || event.content || undefined,
    imageUrl,
    gradient,
    trackRefs,
    tags,
    isPublic,
    isCollaborative,
    createdAt: event.created_at,
  };
}
